import interfaceApi from '@/api/admin/interfaceApi'
import { ArrowDownIcon, ArrowUpIcon, CheckCircleIcon } from '@/components/icons'
import CautionIcon from '@/components/icons/CautionIcon'
import { CONFIG_SETUP_ROUTES, OPERATIONS_ROUTES } from '@/constant/route'
import { useAuthContext } from '@/features/auth/context'
import useTranslation from '@/hooks/useTranslation'
import { selectUser } from '@/store/selectors/UserSelector'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { selectInterfacesAll } from '@/store/slices/interface/interfaceSlice'
import {
  selectInterfacesByOrganization,
  selectInterfacesByOrganizationLastFetched,
  selectInterfacesByOrganizationLoading,
} from '@/store/slices/interfaceByOrganization/interfaceByOrganizationSlice'
import { useAppDispatch, useAppSelector } from '@/store/stores'
import { mode_type } from '@/theme/types/constants'
import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  SxProps,
  TextField,
  Typography,
} from '@mui/material'
import _ from 'lodash'
import clsx from 'clsx'
import { useSetAtom } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { interfaceName } from '../store'
import classes from './styles.module.scss'

const childMenuStyle = {
  padding: '0px',
  '& .MuiMenuItem-root:selected': {
    backgroundColor: 'red',
  },
  '& .MuiMenuItem-root.Mui-selected p': {
    color: 'primary.main',
  },
  '& .MuiMenuItem-root:hover, & .MuiMenuItem-root.Mui-selected': {
    backgroundColor: 'rgba(255, 255, 255, 0.10)',
  },
}

interface SelectLabelDashboardProps {
  sx?: SxProps
  sxText?: SxProps
  isSetup?: boolean
  width?: string
  colorIcon?: string
  setupFirst?: boolean
  onOpen?: () => void
  onClose?: () => void
  id?: string
  menuProps?: Record<string, any>
}

const SelectLabelDashboard = ({
  sx,
  isSetup,
  sxText,
  width,
  colorIcon,
  setupFirst = false,
  onOpen,
  onClose,
  menuProps,
  ...rest
}: SelectLabelDashboardProps) => {
  const { t } = useTranslation()

  const themeMode = useSelector(selectTheme)
  const {
    stationId,
    interfaceId,
    setInterfaceId,
    setStationId,
    setStatusInterfaceId,
    removeInterfaceId,
  } = useAuthContext()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const open = Boolean(anchorEl)
  const setSiteName = useSetAtom(interfaceName)

  const dispatch = useAppDispatch()
  const user = useAppSelector(selectUser)
  const dataInterface = useAppSelector(selectInterfacesAll)
  const dataList = useAppSelector(selectInterfacesByOrganization)
  const isLoading = useAppSelector(selectInterfacesByOrganizationLoading)
  const lastFetched = useAppSelector(selectInterfacesByOrganizationLastFetched)
  const isSuperUserOrUser = user?.role === 'User'

  const filteredDataList = useMemo(() => {
    const validDataList = dataList || []
    const filtered = [...validDataList]
    return filtered.filter((item: any) =>
      (item.customName || item.name)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    )
  }, [dataList, searchTerm])

  const selectedInterface = useMemo(() => {
    return dataList?.find((i: any) => i.id === interfaceId)
  }, [dataList, interfaceId])

  const handleCloseInterface = () => {
    setAnchorEl(null)
    if (onClose) onClose()
  }

  const handleSelectItem = async (item: any) => {
    handleSetAuthContext(item)
    handleCloseInterface()
    if (onClose) onClose()
    // Verify status via API before navigation
    const { data } = await interfaceApi.getById(item.id)
    const isSetUp = data?.status === true

    /**
     * SCRUM-1032 QC item 16 — a controller that is set up opens, it does not
     * go back to setup.
     *
     * `setupFirst` used to send every selection to the setup route, status
     * fetched on the line above and then ignored. Picking a finished
     * controller from the Getting Started page landed on a setup page with
     * nothing to do on it and no way out — which is what Wei Woon hit.
     *
     * A hard navigation rather than `navigate()`: this is the same transition
     * the branch below has always handled with a full reload, and everything
     * holding data for the previous controller wants re-reading.
     */
    if (isSetUp && (setupFirst || isSetup)) {
      window.location.href = OPERATIONS_ROUTES.index.to
      return
    }

    if (setupFirst || (!isSetUp && !isSetup)) {
      navigate(CONFIG_SETUP_ROUTES.setupFirst.to(item.id || interfaceId))
    } else {
      window.location.reload()
    }
  }
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    if (onOpen) onOpen()
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()
  }

  const handleSetAuthContext = (item: any) => {
    setInterfaceId(item.id)
    setStatusInterfaceId(item.status)
    // TODO_PERMISSIONS: dispatch(fetchUserPermissions(item.id))
  }

  useEffect(() => {
    const initializeInterface = async () => {
      // Skip initialization until interface data has been fetched at least once
      // Prevents race condition: on reload, interfaceId exists in localStorage
      // but dataList is still empty (initial state) -> incorrectly clears interfaceId
      // -> then when data loads, auto-selects first item instead of user's choice
      if (isLoading || !lastFetched) return

      let newInterfaceId = interfaceId
      if (!interfaceId && dataList?.length) {
        const interfaceDefault = dataList?.[0]
        let selectedInterfaceId: number

        if (isSuperUserOrUser && dataList) {
          selectedInterfaceId = dataList[0].id
          setInterfaceId(selectedInterfaceId)
          newInterfaceId = selectedInterfaceId
        } else {
          selectedInterfaceId = interfaceDefault.id
          setInterfaceId(selectedInterfaceId)
          newInterfaceId = selectedInterfaceId
        }

        // Verify status via API before navigation
        const { data } = await interfaceApi.getById(String(selectedInterfaceId))
        if (data?.status !== true && !isSetup) {
          navigate(CONFIG_SETUP_ROUTES.setupFirst.to(selectedInterfaceId + ''))
        }
      }

      if (!!interfaceId) {
        if (!!dataList?.length) {
          const existingInterface = dataList.find(
            (item: any) => item.id === interfaceId,
          )
          if (!existingInterface) {
            newInterfaceId = null
          } else if (existingInterface.status === false && !isSetup) {
            // Redirect to setup if stored interface is pending
            navigate(CONFIG_SETUP_ROUTES.setupFirst.to(interfaceId + ''))
            return
          }
        } else {
          newInterfaceId = null
        }
      }
      if (!newInterfaceId) {
        removeInterfaceId()
      }

      setSiteName(newInterfaceId)
    }

    initializeInterface()
  }, [
    isSetup,
    dataList,
    interfaceId,
    isSuperUserOrUser,
    isLoading,
    lastFetched,
  ])

  useEffect(() => {
    const validateInterface = async () => {
      if (_.size(dataInterface) > 0 && interfaceId) {
        const matchingInterface = dataInterface.find(
          (item: any) => item.id === interfaceId,
        )

        // If the stored interfaceId doesn't exist in available interfaces, clear it and let the first useEffect handle setting a valid one
        if (!matchingInterface) {
          setInterfaceId(null)
          return
        }

        const weatherStationId =
          matchingInterface?.weatherLinkStations?.find(
            (item: { sourceName: string }) => item.sourceName === 'WeatherLink',
          )?.weatherLinkStationId || []

        setStationId(weatherStationId + '')
      }
    }

    validateInterface()
  }, [dataInterface, interfaceId])

  if (isLoading) {
    return <CircularProgress size={30} sx={{ color: 'white' }} />
  }

  return (
    <Box sx={sx} className={clsx({ [classes.isSetup]: isSetup })}>
      <Button
        className="select-label__dashboard"
        disableRipple
        sx={{
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '260px',
          padding: '11px 16px',
          height: '40px',
          backgroundColor: 'rgba(255, 255, 255, 0.10)',
          boxSizing: 'border-box',
          '&:hover, &:active, &:focus': {
            backgroundColor: 'rgba(255, 255, 255, 0.10)',
            cursor: 'pointer',
          },
          ...sx,
        }}
        onClick={handleClick}
        endIcon={
          open ? (
            <ArrowUpIcon
              sx={{
                color: colorIcon ? colorIcon : 'primary.contrastText',
              }}
            />
          ) : (
            <ArrowDownIcon
              sx={{
                color: colorIcon ? colorIcon : 'primary.contrastText',
              }}
            />
          )
        }
        {...rest}
      >
        <Typography
          noWrap
          color={isSetup ? 'primary.contrastText' : '#fff'}
          sx={{
            fontSize: '16px',
            fontWeight: '500',
            ...sxText,
          }}
          className={classes.value}
        >
          {selectedInterface?.customName || selectedInterface?.name}
        </Typography>
      </Button>
      <Menu
        className="select-label__wrap__item"
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseInterface}
        sx={{
          '& .MuiPaper-root': {
            boxShadow: 'unset !important',
            padding: '0px',
            width: width,
            mt: '5px',
          },
          '&.select-label__wrap__item': {
            zIndex: 99999,
          },
        }}
        {...menuProps}
      >
        <MenuList
          sx={{
            backgroundColor: 'background.overrideDefault',
            padding: '0px',
            '&& .MuiList-root': {
              paddingTop: '0px',
              paddingBottom: '0px',
              paddingRight: '0px',
            },
          }}
        >
          <Box sx={{ padding: '8px' }}>
            <TextField
              placeholder={t('interfaceSearchPlaceholder')}
              value={searchTerm}
              onKeyDown={onKeyDown}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              size="small"
              sx={{
                backgroundColor: 'background.default',
                borderRadius: '8px',
                border: {
                  ...(themeMode === mode_type.dark
                    ? { border: '1px solid #283d43' }
                    : { border: '1px solid #D7D7D7' }),
                },
                color: 'text.main',
                '& .MuiInputBase-input::placeholder': {
                  color: 'text.main',
                  opacity: 1,
                },
              }}
            />
          </Box>
          <Box
            sx={{
              ...childMenuStyle,
            }}
          >
            {filteredDataList?.map((item: any, key: any) => (
              <MenuItem
                className="select-label__item"
                onClick={() => handleSelectItem(item)}
                key={key}
                selected={item?.id === interfaceId}
                sx={{
                  // width: '322px',
                  width: width,
                  borderBottom: '1px solid #D7D7D7',
                  '&:last-of-type': {
                    borderBottom: 'none',
                  },
                }}
              >
                <Box sx={{ display: 'block' }} width="100%">
                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: '500',
                        ...(width === '218px' && {
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          maxWidth: '160px',
                          overflow: 'hidden',
                        }),
                      }}
                    >
                      {item.customName || item.name}
                    </Typography>
                    {item.state === 'Online' ? (
                      <CheckCircleIcon />
                    ) : (
                      <CautionIcon sx={{ color: 'red' }} />
                    )}
                  </Stack>

                  {item.status === true ? (
                    <Typography
                      sx={{ display: 'block', color: 'primary.contrastText' }}
                    >
                      {t('statusActive')}
                    </Typography>
                  ) : (
                    <Typography sx={{ display: 'block' }}>
                      {t('pendingSetup')}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
            {filteredDataList && filteredDataList.length === 0 && (
              <Stack alignItems="center">
                <Typography className={classes.txtNoOptions}>
                  {t('formsSelectNoOptions')}
                </Typography>
              </Stack>
            )}
          </Box>
        </MenuList>
      </Menu>
    </Box>
  )
}

export default SelectLabelDashboard
